import {createSelector} from '@reduxjs/toolkit';
import {RootState} from '../store';

const signupStateSelector = (state: RootState) => state.signup;

export const signupDataSelector = (state: RootState) => signupStateSelector(state).data;
export const startSignupApiSelector = (state: RootState) => signupStateSelector(state).api.start;
export const signupStatusApiSelector = (state: RootState) => signupStateSelector(state).api.status;
export const resendSignupLinkApiSelector = (state: RootState) => signupStateSelector(state).api.resend;

/**
 * Which screen the visitor is on. Derived, never stored — the same rule the workspace row follows,
 * for the same reason: a stored phase is one more thing to keep in step with the server's answer.
 *
 *   form            nothing started, or they chose to start over
 *   awaitingPayment token minted, Paddle has not reported a completed checkout
 *   ready           paid — go and check your inbox
 *   expired         nobody finished checkout in time; the token is dead
 *
 * **Paying ends the flow.** There is deliberately no phase between "card accepted" and "account
 * exists": whether the webhook has landed yet is our problem, the visitor's next move is their
 * inbox either way, and nobody sits in front of a spinner waiting on someone else's queue. The
 * status poll carries on quietly behind the ready screen — see `signupLinkSentSelector`.
 */
export type SignupPhase = 'form' | 'awaitingPayment' | 'ready' | 'expired';

export const signupPhaseSelector = createSelector(
  [signupDataSelector],
  (data): SignupPhase => {
    if (!data.token) {
      return 'form';
    }
    // Paid outranks every status the server can return, including `expired`: telling someone who
    // has just been charged that "nothing was charged" would be the worst thing this screen could
    // say, and `failed` is a retry in progress rather than a dead end.
    if (data.paid || data.status === 'provisioned') {
      return 'ready';
    }
    if (data.status === 'expired') {
      return 'expired';
    }
    return 'awaitingPayment';
  }
);

/**
 * The one-time login link has actually been emailed.
 *
 * The only thing this gates is the resend button — `/signup/resend-link` answers 409
 * `not_provisioned` before there is an account to sign in to, so offering it early would hand the
 * visitor a button that can only fail. It is not a reason to change what the screen says.
 */
export const signupLinkSentSelector = createSelector(
  [signupDataSelector],
  data => data.status === 'provisioned'
);
